import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, ChevronRight, ChevronLeft, Check, X } from "lucide-react";
import PlaceInput from "@/components/PlaceInput";
import { passwordStrengthErrors, passwordStrengthScore } from "@/lib/password";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    birth_dob: "", birth_time: "", birth_place: "",
  });
  const [city, setCity] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);

  const pwErrors = passwordStrengthErrors(form.password);
  const pwScore = passwordStrengthScore(form.password);

  const handleNext = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError("Please fill name, email, and password");
      return;
    }
    if (pwErrors.length > 0) {
      setError(`Password doesn't meet requirements: ${pwErrors.join(", ")}`);
      return;
    }
    setError("");
    setStep(2);
  };

  const submit = async (withBirth = true) => {
    setError("");
    setLoading(true);
    try {
      const payload = {
        email: form.email, password: form.password, name: form.name,
      };
      if (withBirth && form.birth_dob) {
        payload.birth_dob = form.birth_dob;
        payload.birth_time = form.birth_time || null;
        if (city) {
          payload.birth_lat = city.lat;
          payload.birth_lon = city.lon;
          payload.birth_tz = city.tz;
          payload.birth_place = `${city.name}${city.country ? `, ${city.country}` : ""}`;
        }
      }
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 starfield relative">
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 w-full max-w-md glass-strong rounded-2xl p-8 sm:p-10">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <Sparkles className="w-6 h-6 text-gold" />
          <span className="font-serif text-2xl text-slate-50">Cosomic Sage</span>
        </Link>
        <h1 className="font-serif text-3xl text-center text-slate-50 mb-1">
          {step === 1 ? "Create your account" : "Your birth details"}
        </h1>
        <p className="text-center text-slate-400 text-sm mb-6">
          {step === 1
            ? "Begin your personalized cosmic journey"
            : "Optional — used for charts & Astro Talk context"}
        </p>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`h-1.5 w-8 rounded-full ${step >= 1 ? "bg-amber-500" : "bg-white/10"}`} />
          <div className={`h-1.5 w-8 rounded-full ${step >= 2 ? "bg-amber-500" : "bg-white/10"}`} />
        </div>

        {step === 1 ? (
          <form onSubmit={handleNext} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="register-name-input" required
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" placeholder="Your full name" />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="register-email-input" required
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                onFocus={() => setPwFocused(true)}
                data-testid="register-password-input"
                required
                minLength={8}
                className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
                placeholder="8+ chars, mix of upper/lower/number/special"
              />
              {(pwFocused || form.password) && (
                <div className="mt-2 space-y-1.5" data-testid="password-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition ${
                          i < pwScore
                            ? pwScore >= 5 ? "bg-emerald-500" : pwScore >= 3 ? "bg-amber-500" : "bg-rose-500"
                            : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                  <ul className="text-xs space-y-0.5 mt-1.5">
                    {[
                      "At least 8 characters",
                      "One uppercase letter (A-Z)",
                      "One lowercase letter (a-z)",
                      "One digit (0-9)",
                      "One special character (!@#$…)",
                    ].map((rule) => {
                      const passed = !pwErrors.includes(rule);
                      return (
                        <li key={rule} className={`flex items-center gap-1.5 ${passed ? "text-emerald-400" : "text-slate-500"}`}>
                          {passed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {rule}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            {error && <div className="text-sm text-rose-400" data-testid="register-error">{error}</div>}
            <Button type="submit" data-testid="register-next-button"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium py-5 gap-2">
              Continue <ChevronRight className="w-4 h-4" />
            </Button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="register-form-step2">
            <div>
              <Label className="text-slate-300">Date of Birth</Label>
              <Input type="date" value={form.birth_dob} onChange={(e) => setForm({ ...form, birth_dob: e.target.value })}
                     data-testid="register-dob-input"
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Time of Birth</Label>
              <Input type="time" value={form.birth_time} onChange={(e) => setForm({ ...form, birth_time: e.target.value })}
                     data-testid="register-time-input"
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Birth Place</Label>
              <div className="mt-1.5">
                <PlaceInput value={city} onChange={setCity} testId="register-city" />
              </div>
            </div>
            {error && <div className="text-sm text-rose-400" data-testid="register-error-2">{error}</div>}
            <div className="flex gap-2">
              <Button type="button" onClick={() => setStep(1)} variant="outline"
                      className="rounded-full border-white/10 text-slate-300 gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
              <Button onClick={() => submit(true)} disabled={loading} data-testid="register-submit-button"
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium py-5">
                {loading ? "Creating..." : "Create Account"}
              </Button>
            </div>
            <button type="button" onClick={() => submit(false)} disabled={loading}
                    data-testid="register-skip-birth"
                    className="w-full text-sm text-slate-400 hover:text-amber-300 transition pt-2">
              Skip — I'll add birth details later
            </button>
          </div>
        )}

        {step === 1 && (
          <>
            <div className="flex items-center my-6 gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-slate-500">OR</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <Button onClick={handleGoogle} variant="outline" data-testid="register-google-button"
                    className="w-full rounded-full border-white/15 text-slate-200 hover:bg-white/5 hover:text-amber-300 py-5">
              Continue with Google
            </Button>
          </>
        )}

        <p className="text-center text-sm text-slate-400 mt-6">
          Already a member?{" "}
          <Link to="/login" className="text-amber-400 hover:text-amber-300" data-testid="register-to-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
